import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  addToast,
  Button,
  DateInput,
  Select,
  SelectOptionType,
  TextInput,
  TextOutlinedInput,
} from '@octano/global-ui';
import { useCallback, useMemo, useRef } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Card, CardHeader, Col, Form, Row } from 'reactstrap';
import { v4 as uuidv4 } from 'uuid';
import GoBackButton from '../../../../../../components/buttons/GoBackButton';
import { PathsLayouts } from '../../../../../../config/routes';
import { useValidations } from '../../../../../../hooks/useValidations';
import { TranslationsKeys } from '../../../../../../locales/translations';
import { SurveyContext } from '../../../types/Survey';
import { QuestionType } from '../../../types/SurveyDraft';
import { SectionForm } from '../../../types/types';
import useActions, { ActionType } from '../hooks/useActions';
import ConfirmModal, { ConfirmModalRef } from './ConfirmModal';
import ConflictModal, { ConflictModalRef } from './ConflictModal';

import { useSurveyLoader } from './SurveyCreateLoader';
import SurveySection from './SurveySection';

export interface FormValues {
  id?: string;
  context: SelectOptionType | null;
  title: string;
  period: SelectOptionType | null;
  scale: SelectOptionType | null;
  startDate: string;
  endDate: string;
  sections: SectionForm[];
}

interface FormContainerProps {
  defaultValues?: Partial<FormValues>;
  isPublished?: boolean;
  isFinished?: boolean;
}

type SubmitAction = 'publish' | 'draft';

const FormContainer = ({
  defaultValues,
  isPublished,
  isFinished,
}: FormContainerProps) => {
  const history = useHistory();
  const { data } = useSurveyLoader();
  const { msgValidations } = useValidations();
  const { t } = useTranslation(TranslationsKeys.MANAGEMENT_TOOLS_SURVEY);
  const confirmRef = useRef<ConfirmModalRef>(null);
  const conflictRef = useRef<ConflictModalRef>(null);

  const actions = useActions({
    onSuccess: (type: ActionType) => {
      if (type === 'createSurveyDraft') {
        addToast({
          icon: 'success',
          color: 'success',
          text: t('surveyCreation.creationMessage.draft.success'),
        });
        history.push(`${PathsLayouts.surveys}/draft`);
        return;
      }
      if (type === 'updateDraft') {
        addToast({
          icon: 'success',
          color: 'success',
          text: t('surveyCreation.updateMessage.draft.success'),
        });
        history.push(`${PathsLayouts.surveys}/draft`);
        return;
      }
      if (type === 'createSurvey') {
        addToast({
          icon: 'success',
          color: 'success',
          text: t('surveyCreation.creationMessage.published.success'),
        });
        history.push(`${PathsLayouts.surveys}/published`);
        return;
      }
      if (type === 'updateSurvey') {
        addToast({
          icon: 'success',
          color: 'success',
          text: t('surveyCreation.updateMessage.published.success'),
        });
        history.push(`${PathsLayouts.surveys}/published`);
        return;
      }
    },
    onError: ({ status }) => {
      if (status === 409) {
        conflictRef.current?.openModal();
        return;
      } else
        addToast({
          icon: 'error',
          color: 'danger',
          text: 'Ha ocurrido un error',
        });
    },
  });

  const periodOptions = useMemo(
    () =>
      data!.periods.map((period) => ({
        label: period.name,
        value: period.id,
        startDate: period.startDate,
        endDate: period.endDate,
      })),
    [data],
  );

  const scaleOptions = useMemo(
    () =>
      data!.scales.map((scale) => ({
        label: scale.title,
        value: scale.id,
      })),
    [data],
  );

  const surveyContextOptions = useMemo(
    () =>
      data!.surveyContexts.map((context) => ({
        label: context.name,
        value: context.id,
      })),
    [data],
  );

  const {
    control,
    watch,
    handleSubmit,
    formState: { errors },
    clearErrors,
    setError,
  } = useForm<FormValues>({
    defaultValues: {
      title: '',
      context: null,
      period: null,
      scale: null,
      startDate: '',
      endDate: '',
      sections: [{ questions: [{}] }],
      ...defaultValues,
    },
  });

  const startDate = watch('startDate');
  const scale = watch('scale');

  const optionsFromScale = useMemo(
    () =>
      scale ? data!.scales.find((s) => s.id === scale.value)?.scaleOptions : [],
    [scale, data],
  );

  const { fields: sections, append, remove, move } = useFieldArray({
    control,
    name: 'sections',
  });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleSectionDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = sections.findIndex((s) => s.id === active.id);
      const newIndex = sections.findIndex((s) => s.id === over.id);
      move(oldIndex, newIndex);
    }
  };

  const validateSections = (data: FormValues) => {
    clearErrors();
    let hasErrors = false;

    if (data.sections.length === 0) {
      setError('sections', {
        type: 'custom',
        message: t('validation.minSections'),
      });
      hasErrors = true;
    }

    data.sections.forEach((section, index) => {
      if (!section.questions || section.questions.length === 0) {
        setError(`sections.${index}.questions`, {
          type: 'custom',
          message: t('createSurvey.validation.minQuestions'),
        });
        hasErrors = true;
      }
    });

    return !hasErrors;
  };

  const handleSaveDraft = handleSubmit((data) => onSubmit(data, 'draft'));
  const handlePublish = handleSubmit((data) => onSubmit(data, 'publish'));
  const handleCancel = () => {
    if (!isPublished && !isFinished) {
      history.push(`${PathsLayouts.surveys}/draft`);
      return;
    }

    history.push(`${PathsLayouts.surveys}`);
  };

  const onSubmit = (data: FormValues, action: SubmitAction) => {
    const isValid = validateSections(data);
    if (!isValid) return;
    const formatedData = {
      title: data.title,
      startDate: data.startDate,
      endDate: data.endDate,
      context: data.context?.value as SurveyContext,
      content: {
        period: {
          id: Number(data.period?.value),
          name: String(data.period?.label),
        },
        sections: data.sections.map((section) => ({
          name: section.title,
          description: section.description,
          questions: section.questions.map((question, index) => {
            return {
              text: question.text,
              order: index,
              type: QuestionType.SingleChoice,
              scale: {
                id: String(data.scale?.value),
              },
            };
          }),
        })),
      },
    };

    switch (action) {
      case 'publish':
        confirmRef.current?.openModal({
          formData: formatedData,
          id: data.id,
        });
        break;
      case 'draft':
        if (data.id) {
          actions.updateDraft(data.id, formatedData);
          break;
        }
        actions.createDraft(formatedData);
        break;
    }
  };

  const addSection = () => {
    append({
      id: uuidv4(),
      title: `Sección ${sections.length + 1}`,
      description: '',
      questions: Array.from({ length: 5 }, () => ({ id: uuidv4(), text: '' })),
    });
  };

  const backToListGradeList = useCallback(() => {
    if (!isPublished && !isFinished) {
      history.push(`${PathsLayouts.surveys}/draft`);
      return;
    }
    history.push(`${PathsLayouts.surveys}`);
  }, [history, isFinished, isPublished]);

  return (
    <Card className=" m-3">
      <CardHeader className="bg-white border-0 pt-5">
        <Col className="mb-4">
          <GoBackButton onClick={backToListGradeList} />
        </Col>

        {!isPublished && (
          <>
            <p className="mb-0 fs-20 font-weight-bold text-primary">
              {t('surveyCreation.header.surveyCharacteristics')}
            </p>
            <p className="mb-0 text-muted fs-16 mb-5">
              {t('surveyCreation.header.completeFields')}
            </p>
          </>
        )}
        <p className="mb-0 fs-20 font-weight-bold text-primary">
          {t('surveyCreation.titleAndType.title')}
        </p>
        <p className="mb-0 text-muted fs-16">
          {t('surveyCreation.titleAndType.description')}
        </p>
      </CardHeader>
      <Form>
        <Row className="px-4">
          <Col xs={12} md={8}>
            <TextInput
              name="title"
              control={control}
              label={t('surveyCreation.formFields.title.label')}
              type="text"
              placeholder={t('surveyCreation.formFields.title.placeholder')}
              rules={{
                required: msgValidations.required,
              }}
              readOnly={isFinished}
            />
          </Col>
          <Col xs={12} md={8} className="mb-4">
            <Select
              name="context"
              label={t('surveyCreation.formFields.surveyType.label')}
              control={control}
              options={surveyContextOptions}
              rules={{
                required: msgValidations.required,
              }}
              disabled={isPublished}
            />
          </Col>
          <Col xs={12}>
            <p className="mb-0 fs-20 font-weight-bold text-primary">
              {t('surveyCreation.periodSection.title')}
            </p>
            <p className="mb-0 text-muted fs-16">
              {t('surveyCreation.periodSection.description')}
            </p>
          </Col>
          <Col xs={12} md={8}>
            <Select
              name="period"
              control={control}
              label={t('surveyCreation.formFields.period.label')}
              options={periodOptions}
              rules={{
                required: msgValidations.required,
              }}
              disabled={isPublished}
            />
          </Col>
        </Row>
        <Row className="px-4">
          <Col xs={12} md={4} className="mb-4">
            <DateInput
              label={t('surveyCreation.formFields.dates.startDate')}
              name="startDate"
              control={control}
              minDate={new Date()}
              rules={{
                required: msgValidations.required,
              }}
              disabled={isFinished}
            />
          </Col>
          <Col xs={12} md={4} className="mb-4">
            <DateInput
              label={t('surveyCreation.formFields.dates.endDate')}
              name="endDate"
              control={control}
              minDate={new Date(startDate)}
              disabled={!startDate || isFinished}
              rules={{
                required: msgValidations.required,
              }}
            />
          </Col>

          <Col xs={12}>
            <p className="mb-0 fs-20 font-weight-bold text-primary">
              {t('surveyCreation.scaleSection.title')}
            </p>
            <p className="mb-0 text-muted fs-16">
              {t('surveyCreation.scaleSection.description')}
            </p>
          </Col>
          <Col xs={12} md={8} className="mb-4">
            <Select
              name="scale"
              control={control}
              label={t('surveyCreation.formFields.scale.label')}
              options={scaleOptions}
              rules={{
                required: msgValidations.required,
              }}
              disabled={isPublished}
            />
          </Col>
          <Col xs={12} md={8} className="pl-4">
            {Array.isArray(optionsFromScale) &&
              optionsFromScale.map((option) => (
                <Row key={`row-${option.id}`} className="mb-3">
                  <Col xs={1}>
                    <div
                      style={{ height: 40 }}
                      className="d-flex justify-content-center align-items-center bg-light"
                    >
                      {option.optionNumber ?? ''}
                    </div>
                  </Col>
                  <Col xs={11}>
                    <TextOutlinedInput
                      name={`scaleDescription-${option.id}`}
                      label=""
                      value={option.description ?? ''}
                      readOnly
                    />
                  </Col>
                </Row>
              ))}
          </Col>
          <Col xs={12} md={8}>
            <p className="mb-0 fs-20 font-weight-bold text-primary">
              {t('surveyCreation.questionsSection.title')}
            </p>
            <p className="mb-0 text-muted fs-16">
              {t('surveyCreation.questionsSection.description')}
            </p>
          </Col>
        </Row>

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleSectionDragEnd}
          modifiers={[restrictToVerticalAxis]}
        >
          <SortableContext
            items={sections.map((s) => s.id)}
            strategy={verticalListSortingStrategy}
          >
            {sections.map((section, sectionIndex) => (
              <SurveySection
                key={section.id}
                id={section.id}
                control={control}
                sectionIndex={sectionIndex}
                errors={errors}
                onRemove={() => {
                  if (sections.length === 1) {
                    return;
                  }
                  remove(sectionIndex);
                }}
                disabled={isPublished}
              />
            ))}
          </SortableContext>
        </DndContext>

        <Row className="px-4">
          {errors.sections && (
            <p style={{ color: 'red' }} className="px-4">
              {(errors.sections as any)?.message}
            </p>
          )}
          {!isPublished && (
            <Col xs={12} md={8} className="">
              <Button
                icon="plus"
                color="primary"
                onClick={addSection}
                fullwidth
                size="md"
                text={t('surveyCreation.actions.addSection')}
              />
            </Col>
          )}
        </Row>

        <Row className="p-4">
          <Col xs={12} className="d-flex justify-content-end mt-4">
            {!isFinished && (
              <Button
                outlined
                className="mr-4"
                color="danger"
                text={t('surveyCreation.actions.cancel')}
                onClick={handleCancel}
                style={{ width: '100%', maxWidth: 208 }}
              />
            )}
            {!isPublished && (
              <Button
                outlined
                className="mr-4"
                color="secondary"
                text={t('surveyCreation.actions.saveDraft')}
                onClick={handleSaveDraft}
                style={{ width: '100%', maxWidth: 208 }}
              />
            )}
            {!isFinished && (
              <Button
                color="secondary"
                text={t('surveyCreation.actions.publish')}
                onClick={handlePublish}
                style={{ width: '100%', maxWidth: 208 }}
              />
            )}
          </Col>
        </Row>
      </Form>
      <ConfirmModal
        ref={confirmRef}
        onConfirm={(data) => {
          if (isPublished && data.id) {
            actions.updateSurvey(data.id!, data.formData);
          } else {
            actions.createSurvey(data.formData);
          }
        }}
        onDecline={() => {}}
      />
      <ConflictModal ref={conflictRef} />
    </Card>
  );
};

export default FormContainer;
